import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';
import { readCkScope, scopeKey, useCkScope } from './CkOperationsShared';
import { apiBase, authHeaders } from '../utils/authHeaders';

/**
 * Owner / department daily work queue (OWN01).
 *
 * The load-bearing behaviour is the coverage block. When a source could not be
 * read the queue says so and the count is withheld — an owner must never read a
 * clean board built on an unread source. "Missing is not zero."
 */
export default function OwnerQueue() {
  const scope = useCkScope();
  return <Surface key={scopeKey(scope)} scope={scope} />;
}

function Surface({ scope }) {
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const current = readCkScope();
      const headers = authHeaders({ orgId: current.organizationId, locationId: current.locationId });
      const [q, m] = await Promise.all([
        fetch(`${apiBase()}/api/owner/queue`, { headers }),
        fetch(`${apiBase()}/api/owner/board-metrics`, { headers }),
      ]);
      if (!alive.current) return;
      if (q.status === 403) {
        setError('Your role cannot view the owner queue.');
        return;
      }
      if (!q.ok) throw new Error(`Queue request failed (${q.status})`);
      setData(await q.json());
      if (m.ok) setMetrics(await m.json());
    } catch (e) {
      if (alive.current) setError(e.message || 'Could not load the queue');
    } finally {
      if (alive.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  const coverage = data?.coverage;
  const incomplete = coverage && coverage.complete === false;

  return (
    <main className="owner-queue">
      <header>
        <h1>Daily work queue</h1>
        <button onClick={load} disabled={busy} aria-label="Refresh"><RefreshCw size={18} /></button>
      </header>

      {error && <p role="alert">{error}</p>}

      {incomplete && (
        <p role="alert" className="owner-queue-coverage-warning">
          <AlertTriangle size={16} /> Some sources could not be read, so the total below is
          withheld. A count shown here would be a guess, not a fact. Unread:{' '}
          {(coverage.sources_unavailable || []).join(', ')}.
        </p>
      )}

      {data && (
        <section aria-label="Queue summary">
          <h2>Open items</h2>
          <p className="owner-queue-count">
            {coverage?.exception_count_display === null
              ? 'Withheld — coverage incomplete'
              : `${coverage?.exception_count_display ?? data.exception_count} open`}
          </p>
          {data.verification_required && (
            <p role="status"><ShieldCheck size={16} /> Nothing is open. Verify against your sources before closing the day.</p>
          )}
        </section>
      )}

      {data?.exceptions?.length > 0 && (
        <ul className="owner-queue-list">
          {data.exceptions.map((e) => (
            <li key={`${e.source}:${e.ref}`} data-severity={e.severity}>
              <strong>{e.title}</strong>
              <span> {e.kind.replace(/_/g, ' ')}</span>
              <small>
                owner {e.accountable_owner || 'unassigned'}
                {e.deputy ? ` · deputy ${e.deputy}` : ''}
                {e.due_at ? ` · due ${e.due_at}` : ' · no due date'}
              </small>
              {e.verification && <em>Verification: {e.verification}</em>}
            </li>
          ))}
        </ul>
      )}

      {metrics && (
        <section aria-label="Board metrics">
          <h2>Board metrics</h2>
          <p>Reconciled to source: {metrics.reconciled ? 'yes' : 'no'}</p>
          <ul>
            {Object.entries(metrics.metrics || {}).map(([name, m]) => (
              <li key={name}>
                {name.replace(/_/g, ' ')}:{' '}
                {m.value === null
                  ? <em>unknown — source unavailable (not zero)</em>
                  : m.value}
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
